<script setup lang="ts">
import { Sketch } from '@ckpack/vue-color'
import { useTranslations } from '@/composables/useTranslations.ts'
import { ref, watch } from 'vue'
import { isEmpty, isFunction, isObject } from 'lodash'
import type { ColorPickerColor } from '@/components/projects/interfaces.ts'

enum EmitEvent {
  Saved = 'saved',
  Change = 'change',
  Cancel = 'cancel'
}

const { t } = useTranslations()
const props = defineProps<{
  change?: (value: ColorPickerColor) => void
  save?: (value: ColorPickerColor) => void
  cancel?: () => void
}>()
const emit = defineEmits([EmitEvent.Saved, EmitEvent.Change, EmitEvent.Cancel])
const model = ref<ColorPickerColor | Record<string, any>>({})

watch(model, (newValue) => {
  if (!isEmpty(newValue)) {
    emit(EmitEvent.Change, newValue)
  }
}, {
  immediate: true
})
</script>

<template>
  <div class="color-selector-picker" @click.stop>
    <!-- @vue-expect-error type conflict -->
    <Sketch v-model="model" class="shadow-none"/>
    <div class="flex gap-2 justify-between">
      <button class="action-btn cancel-btn" @click="emit(EmitEvent.Cancel)">
        {{ t('common.buttons.cancel') }}
      </button>
      <button class="action-btn save-btn" @click="emit(EmitEvent.Saved, model)" :disabled="isEmpty(model)">
        {{ t('common.buttons.save') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.color-selector-picker {
  @apply bg-white rounded-md overflow-hidden p-1 shadow-lg;
}

.color-selector-picker .vc-sketch {
  @apply shadow-none p-1;
}

.action-btn {
  @apply px-3 py-1 text-sm rounded-md transition-colors w-1/2;
}

.save-btn {
  @apply bg-purpleCustom text-white hover:opacity-85 disabled:opacity-50;
}

.cancel-btn {
  @apply bg-gray-100 text-gray-700 hover:bg-gray-200;
}
</style>
